/**
 * AuiTable.format — generic, extensible cell-value formatting for the Table.
 *
 * A column can declare an explicit `type` (e.g. `'date'`) or, when omitted, the
 * type is auto-detected by sampling the column's raw values. Each registered
 * `ColumnFormatter` turns a raw display string into a human-readable one (e.g.
 * an ISO timestamp into a localized, browser-timezone date).
 *
 * Formatting is a DISPLAY concern only — sorting, filtering, distinct-value
 * listing and CSV export all keep operating on the raw `coerceCellToString`
 * values (see AuiTable.helpers), so ISO strings still sort chronologically and
 * persisted filters stay stable. The renderer applies `formatCellValue` purely
 * for the text shown in cells and filter-menu labels, and offers a per-column
 * toggle back to raw.
 *
 * Adding a new type later is local to this file: implement a `ColumnFormatter`,
 * add its id to `ColumnTypeId`, and push it onto `FORMATTERS`.
 */

/** Known column value types. Grows as formatters are added (`| 'boolean' | …`). */
export type ColumnTypeId = 'date';

export interface ColumnFormatter {
  id: ColumnTypeId;
  /** Does a single non-empty raw value look like this type? */
  detect(rawValue: string): boolean;
  /** Convert a raw display string to its human-readable form. Returns the raw
   *  value unchanged when it can't be formatted. */
  format(rawValue: string): string;
}

// ─── Date formatter ──────────────────────────────────────────────────────────

// Bare calendar date: `2026-06-15`.
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// Date + time, optionally with seconds/fraction and a `Z` or ±HH:MM offset.
// A space separator (`2026-06-15 09:00`) is tolerated alongside `T`.
const ISO_DATETIME =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/** Parse a bare `yyyy-mm-dd` as LOCAL midnight — `new Date('2026-06-15')` would
 *  resolve to the previous day in any negative-UTC-offset zone. Mirrors the
 *  calendar's `parseEventDate` rule. */
function parseDateOnlyLocal(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const DATE_FORMATTER: ColumnFormatter = {
  id: 'date',
  detect(value) {
    if (ISO_DATE_ONLY.test(value)) {
      return !Number.isNaN(parseDateOnlyLocal(value).getTime());
    }
    if (ISO_DATETIME.test(value)) {
      return !Number.isNaN(new Date(value).getTime());
    }
    return false;
  },
  format(value) {
    // Date-only → local calendar day (no timezone shift), date only.
    if (ISO_DATE_ONLY.test(value)) {
      const d = parseDateOnlyLocal(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    // Datetime → instant rendered in the browser's locale + timezone.
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  },
};

// ─── Registry ────────────────────────────────────────────────────────────────

const FORMATTERS: ColumnFormatter[] = [DATE_FORMATTER];

const FORMATTERS_BY_ID = new Map<ColumnTypeId, ColumnFormatter>(
  FORMATTERS.map((f) => [f.id, f]),
);

function isColumnTypeId(value: string | undefined): value is ColumnTypeId {
  return value != null && FORMATTERS_BY_ID.has(value as ColumnTypeId);
}

/**
 * Resolve a column's value type. An explicit, known `type` hint wins. Otherwise
 * the type is auto-detected: a formatter is chosen only when EVERY non-empty
 * sample matches it (and there is at least one non-empty sample), so a single
 * stray value keeps the column unformatted. Returns null when nothing matches.
 */
export function resolveColumnType(
  samples: string[],
  explicit?: string,
): ColumnTypeId | null {
  if (isColumnTypeId(explicit)) return explicit;
  if (explicit != null) return null; // an unknown explicit hint disables detection
  const nonEmpty = samples.filter((s) => s !== '');
  if (nonEmpty.length === 0) return null;
  for (const formatter of FORMATTERS) {
    if (nonEmpty.every((s) => formatter.detect(s))) return formatter.id;
  }
  return null;
}

/** Format a raw display string for the given column type (raw when type null). */
export function formatCellValue(raw: string, type: ColumnTypeId | null): string {
  if (!type) return raw;
  const formatter = FORMATTERS_BY_ID.get(type);
  return formatter ? formatter.format(raw) : raw;
}
